import { cx } from '../../lib/cx.ts';
import { days, moneyCompact } from '../../lib/format.ts';
import { StatusDot } from '../ui/Badge.tsx';
import { AlertIcon, ClockIcon } from '../icons.tsx';

export type RiskLevel = 'none' | 'stalled' | 'at_risk';

export interface PipelineCardData {
  id: number;
  fullName: string;
  practiceName: string | null;
  /** NUMERIC from the API, so a string. Never parsed into a float here. */
  dealValue: string | null;
  daysInStage: number;
  risk: RiskLevel;
  /** The committed next step, or null when nobody owns one. */
  nextEvent: string | null;
}

/**
 * Classes are written out in full. Tailwind scans source statically, so a
 * constructed name like `text-${tone}` produces no CSS at all.
 */
const RISK: Record<
  Exclude<RiskLevel, 'none'>,
  { className: string; icon: typeof AlertIcon; label: string }
> = {
  stalled: { className: 'text-stalled', icon: ClockIcon, label: 'Stalled in stage' },
  at_risk: { className: 'text-risk', icon: AlertIcon, label: 'At risk' },
};

/**
 * Presentational only. Section 3 wires the drag handlers and the click
 * through to the lead. Shows exactly five things, because a board card that
 * tries to show six stops being scannable.
 */
export function PipelineCard({
  data,
  dragging = false,
  onClick,
}: {
  data: PipelineCardData;
  dragging?: boolean;
  onClick?: () => void;
}) {
  const risk = data.risk === 'none' ? null : RISK[data.risk];
  const RiskIcon = risk?.icon;

  return (
    <article
      onClick={onClick}
      className={cx(
        'surface-lift group cursor-pointer rounded-md border border-border bg-surface p-2.5 transition',
        'hover:border-border-strong hover:bg-surface-raised',
        dragging && 'rotate-1 opacity-90 shadow-lg',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-fg">{data.fullName}</p>
          <p className="mt-0.5 truncate text-xs text-fg-subtle">
            {data.practiceName ?? 'Practice unknown'}
          </p>
        </div>
        {risk && RiskIcon && (
          <span className={cx('shrink-0', risk.className)} title={risk.label}>
            <RiskIcon size={13} />
          </span>
        )}
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="numeric text-sm font-semibold tracking-tight text-fg">
          {data.dealValue === null ? '—' : moneyCompact(data.dealValue)}
        </span>
        <span className="numeric flex items-center gap-1 text-xs text-fg-subtle">
          <ClockIcon size={11} />
          {days(data.daysInStage)}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-1.5 border-t border-border-subtle pt-2">
        {data.nextEvent ? (
          <>
            <StatusDot tone="open" />
            <span className="truncate text-xs text-fg-muted">{data.nextEvent}</span>
          </>
        ) : (
          <>
            <StatusDot tone="risk" />
            <span className="text-xs text-risk">No next event owned</span>
          </>
        )}
      </div>
    </article>
  );
}
